import { businessDate, lastBusinessDay } from '@dcp/shared'

import dayjs from '@dcp/shared/utils/dayjs'

/**
 * Formats a Date object to YYYY-MM-DD string in local timezone.
 * Avoids timezone issues that occur with toISOString() which converts to UTC.
 *
 * NOTE: this is a VIEWER-FACING conversion of a value the caller already has
 * (e.g. a day picked in a calendar widget) — it is NOT how to ask what day it
 * is. For that use `businessDate()` from `@dcp/shared`, which answers with the
 * day the company's books are on rather than the day the browser happens to be on.
 *
 * @param date - Date to format
 * @returns Date string in YYYY-MM-DD format
 */
export function toLocalDateString(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD')
}

/**
 * Safely creates a Date from a string, returning undefined for invalid dates.
 *
 * @param dateString - Date string to parse (e.g. "2024-01-15")
 * @returns Parsed Date or undefined if input is falsy/invalid
 */
export function safeParseDate(dateString: string | undefined): Date | undefined {
  if (!dateString) return undefined
  const d = dayjs(dateString)
  return d.isValid() ? d.toDate() : undefined
}

/**
 * Calculates default date range (last 30 days from now).
 *
 * @returns Object with startDate (30 days ago) and endDate (today) as YYYY-MM-DD strings
 */
export function getDefaultDateRange() {
  return {
    startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  }
}

/**
 * Shifts a date-only string forward by one calendar day.
 *
 * Validates the input first, matching the `dayjs().isValid()` convention used by
 * `safeParseDate` above and the import/export date helpers. Without the guard a
 * malformed string would format to `"Invalid Date"` and be sent to the NOT NULL
 * `date` column; throwing instead lets callers surface the failure (e.g. an error
 * toast) rather than silently attempting to persist a bad value.
 *
 * @param date - Date string in YYYY-MM-DD format (e.g. a projection's `date`)
 * @returns The following calendar day, also in YYYY-MM-DD format
 * @throws {Error} If `date` is not a valid date string
 */
export function nextCalendarDay(date: string): string {
  const parsed = dayjs(date)
  if (!parsed.isValid()) {
    throw new Error(`Invalid date: ${date}`)
  }
  return parsed.add(1, 'day').format('YYYY-MM-DD')
}

/** Result of {@link remainderOfWeek}. All dates are ISO YYYY-MM-DD strings. */
export interface RemainderOfWeek {
  /** Query-window start — today (ISO YYYY-MM-DD). Calendar, NOT skipped to a business day. */
  start: string
  /** Query-window end — this week's Saturday (ISO YYYY-MM-DD). US week Sun–Sat. */
  end: string
  /**
   * Business days (Mon–Fri) remaining from today THROUGH Friday, inclusive —
   * drives the "Remainder of Week (N days)" heading (per DCP-143 spec). 0 on
   * Saturday (and on Sunday counts Mon–Fri = 5, since today itself is not a
   * business day but the window still includes the full working week ahead).
   */
  businessDays: number
}

/**
 * Computes the "remainder of the current week" for the Cash Position Summary
 * (DCP-143/DCP-144). The week is US-style Sunday–Saturday.
 *
 * The QUERY WINDOW (`start`/`end`) is the pure CALENDAR range from `today`
 * through this week's Saturday, inclusive — every day counts, with NO
 * business-day filtering. Per the BA, filtering out weekend days silently
 * dropped projections dated on the current week's Saturday (or on Sunday when
 * `today` is itself Sunday), so the window must include every calendar day,
 * not just Mon–Fri. `today` always falls within `[sunday, saturday]` of its
 * own calendar week, so `start` (today) is never after `end` (this week's
 * Saturday) — there is no "Saturday → empty range" edge case to handle.
 *
 * The `businessDays` count is a SEPARATE, decoupled figure used only to drive
 * the "Remainder of Week (N days)" heading text (DCP-143 spec): the number of
 * Mon–Fri weekdays from `today` through this week's Friday, inclusive. It does
 * NOT affect the window above — a weekend-dated projection is still included
 * in the query window even though it doesn't add to this count.
 *
 * Behaviour by weekday (dayjs `.day()`: Sun=0 … Sat=6), for a week running
 * Sun 6/28 … Sat 7/4:
 *   - Sunday    → window Sun–Sat, businessDays 5 (Mon..Fri)
 *   - Wednesday → window Wed–Sat, businessDays 3 (Wed..Fri)
 *   - Friday    → window Fri–Sat, businessDays 1 (Fri)
 *   - Saturday  → window Sat only, businessDays 0 (today is past Friday)
 *
 * WHICH DAY IS "TODAY": the reference instant is resolved through
 * `businessDate()` (`@dcp/shared`), i.e. the calendar day in the timezone the
 * company's books are kept in — NOT the browser's local day, which is what this
 * used to read. The ladder's windows are compared against `projections.date` /
 * `balances.date`, so a user working outside the business timezone would
 * otherwise be shown a different week than the customer's books are on. The
 * business-day count (Mon–Fri) and the query-window rules from DCP-143 are
 * unchanged — only the answer to "which day is today" moved.
 *
 * @param today - Optional reference instant; defaults to now. Only the calendar
 *   day is used (time-of-day is ignored), as resolved in the business timezone.
 * @returns Range descriptor — see {@link RemainderOfWeek}.
 */
export function remainderOfWeek(today: Date = new Date()): RemainderOfWeek {
  // `businessDate` yields a bare `YYYY-MM-DD` calendar date, which dayjs parses
  // at local midnight — so from here on the local calendar fields ARE the
  // business calendar fields and every calculation below is unchanged.
  const ref = dayjs(businessDate(today))

  // Saturday of the current Sun–Sat week. The week's Sunday is `ref` minus its
  // weekday index; Saturday is that Sunday + 6 days; Friday is Sunday + 5.
  const sunday = ref.subtract(ref.day(), 'day')
  const saturday = sunday.add(6, 'day')
  const friday = sunday.add(5, 'day')

  // Count weekdays (Mon–Fri) from today through Friday inclusive. On Saturday,
  // `ref` is already past `friday`, so the loop below never runs and yields 0.
  let businessDays = 0
  let cursor = ref
  while (cursor.isSame(friday, 'day') || cursor.isBefore(friday, 'day')) {
    const dow = cursor.day()
    if (dow !== 0 && dow !== 6) businessDays += 1
    cursor = cursor.add(1, 'day')
  }

  return {
    start: ref.format('YYYY-MM-DD'),
    end: saturday.format('YYYY-MM-DD'),
    businessDays,
  }
}

/**
 * Returns the last expected balance date — the most recent weekday (Mon–Fri) on or
 * before the current BUSINESS day. Used to determine whether a balance is "fresh"
 * for a given facility.
 *
 * **Which calendar:** the day the company's books are on, resolved by
 * `businessDate()` (`@dcp/shared`) rather than by UTC (what this used to do) or by
 * the browser. That is the whole point of the shared primitive: the write path —
 * `balances.upsertManual` in `db-tenant.ts` — stamps `balances.date` with the SAME
 * primitive, so a balance a user enters right now necessarily lands on the day this
 * check asks for, and therefore clears its own missing-data warning. Under the old
 * UTC rule an evening entry in the US was filed under tomorrow and the warning
 * stayed up (DCP-161).
 *
 * **Why Mon–Fri:** Banks post no balances on weekends. Comparing against a Saturday
 * would flag every facility every weekend. There is NO bank-holiday calendar anywhere
 * in this repo, so approximately 10 holiday Mondays a year will still read as missing
 * even though no balance was posted that day — that limitation is accepted.
 *
 * @param now - Reference instant; defaults to `new Date()`. Only the calendar day
 *   is used (time-of-day is ignored), as resolved in the business timezone.
 * @returns The most recent weekday (Mon–Fri) as a YYYY-MM-DD string.
 */
export function lastExpectedBalanceDate(now: Date = new Date()): string {
  return lastBusinessDay(businessDate(now))
}

/**
 * One entry in the Cash Position Summary period ladder (DCP-144). Describes a
 * single forecast window — the remainder of the current week, or one of the four
 * following full calendar weeks — with everything the UI needs to both query the
 * RPC (`start`/`end`) and render its heading (`label` / `days` /
 * `dateRangeLabel`).
 */
export interface CashPositionPeriod {
  /** Stable identifier for the period, used as a selector value and React key. */
  key: 'remainder' | 'week1' | 'week2' | 'week3' | 'week4'
  /** Human label ('Remainder of Week' | '1 Week Out' | … | '4 Weeks Out'). */
  label: string
  /** Inclusive range start (ISO YYYY-MM-DD) — passed to the RPC. */
  start: string
  /** Inclusive range end (ISO YYYY-MM-DD) — passed to the RPC. */
  end: string
  /**
   * Business-day count for the "Remainder of Week (N days)" heading
   * (remainder period only). `null` for the full-week periods, which use
   * {@link dateRangeLabel} instead.
   */
  businessDays: number | null
  /**
   * Display date range 'M/D/YYYY - M/D/YYYY' (no leading zeros, e.g.
   * '4/27/2025 - 5/3/2025') for the full-week periods. Empty string for the
   * 'remainder' period, whose heading uses the day count instead.
   */
  dateRangeLabel: string
}

/**
 * Builds the ordered 5-period ladder for the Cash Position Summary forecast
 * (DCP-144): index 0 is the remainder of the current week, indexes 1–4 are the
 * four following full calendar weeks (US-style Sunday→Saturday).
 *
 * Period 0 ('remainder') reuses {@link remainderOfWeek} so its query window
 * (today through this week's Saturday, inclusive — no business-day filtering)
 * stays identical to the single-week view (DCP-143). Its `dateRangeLabel` is
 * empty because its heading is driven by the business-day count instead.
 *
 * The four week periods are anchored to THIS week's Sunday (`today` minus its
 * weekday index): week1 = [thisSunday+7, thisSunday+13], week2 = +14/+20,
 * week3 = +21/+27, week4 = +28/+34. Each carries a `M/D/YYYY - M/D/YYYY` range
 * label (no leading zeros) matching the spec examples like
 * "1 Week Out (4/27/2025 - 5/3/2025)".
 *
 * WHICH DAY IS "TODAY": as in {@link remainderOfWeek}, the reference instant is
 * resolved through `businessDate()` (`@dcp/shared`) — the day the company's books
 * are on, not the browser's day. The anchoring arithmetic, the DCP-143 business-day
 * count and the DCP-144 label formats are all unchanged.
 *
 * @param today - Optional reference instant; defaults to now. Only the calendar day
 *   is used (time-of-day is ignored), as resolved in the business timezone.
 * @returns The five period descriptors in display order.
 */
export function cashPositionPeriods(today: Date = new Date()): CashPositionPeriod[] {
  // Same bare-calendar-date handoff as remainderOfWeek: parsed at local midnight,
  // so the dayjs arithmetic below operates directly on business calendar fields.
  const ref = dayjs(businessDate(today))

  // Period 0: reuse the DCP-143 remainder-of-week range so the multi-period view
  // and the legacy single-week view agree exactly for the current week.
  const remainder = remainderOfWeek(today)

  // Sunday of the calendar week containing `today` (Sun=0 … Sat=6). Every full
  // week period is `sunday + N*7` (Sunday) through `sunday + N*7 + 6` (Saturday).
  const sunday = ref.subtract(ref.day(), 'day')

  /**
   * Builds one full-week period descriptor `weeksAhead` calendar weeks after the
   * current week (weeksAhead=1 → next week).
   */
  const weekPeriod = (
    key: CashPositionPeriod['key'],
    label: string,
    weeksAhead: number,
  ): CashPositionPeriod => {
    const weekStart = sunday.add(weeksAhead * 7, 'day')
    const weekEnd = weekStart.add(6, 'day')
    return {
      key,
      label,
      start: weekStart.format('YYYY-MM-DD'),
      end: weekEnd.format('YYYY-MM-DD'),
      // Full weeks are labelled by their calendar range, not a business-day count.
      businessDays: null,
      dateRangeLabel: `${weekStart.format('M/D/YYYY')} - ${weekEnd.format('M/D/YYYY')}`,
    }
  }

  return [
    {
      key: 'remainder',
      label: 'Remainder of Week',
      start: remainder.start,
      end: remainder.end,
      businessDays: remainder.businessDays,
      // Heading uses the business-day count, so no range label for the remainder period.
      dateRangeLabel: '',
    },
    weekPeriod('week1', '1 Week Out', 1),
    weekPeriod('week2', '2 Weeks Out', 2),
    weekPeriod('week3', '3 Weeks Out', 3),
    weekPeriod('week4', '4 Weeks Out', 4),
  ]
}
